/**
 * Tutor Exam Review Workspace — Digital SAT and PTE Academic.
 *
 * Endpoints (all under /api/exam/v2):
 *   GET /tutor/students?exam_type=…                    → tutor-scoped student roster
 *   GET /tutor/student/{email}/insights                → deep-dive for a single student
 *   GET /tutor/reviews-inbox?exam_type=…               → attempts awaiting review
 *   POST /attempts/{id}/review                         → feedback + mark reviewed
 *
 * PTE-specific behaviour:
 *   - Inline audio player uses /api/pte/speaking/recording/{rec_id} (auth-guarded).
 *   - Writing responses render inline with word count.
 *   - Tutor sees only their own students (canonical resolver + tutor_email stamp).
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Loader2, Users, Inbox, RefreshCcw, Eye, MessageSquare, CheckCircle2,
  ClipboardList, GraduationCap, Mic, PenTool,
} from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import AdminAssignmentsPanel from '@/components/admin/AdminAssignmentsPanel';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const ax = axios.create({ baseURL: BACKEND_URL, withCredentials: true });

const EXAMS = {
  dsat:         { label: 'Digital SAT',   accent: 'red-700',    reviewRoute: (id) => `/dsat/attempt/${id}/review` },
  pte_academic: { label: 'PTE Academic',  accent: 'emerald-700', reviewRoute: null /* inline drawer */ },
};

export default function TutorDSATPage({ initialExam = 'dsat' }) {
  const [examType, setExamType] = useState(initialExam);
  const [tab, setTab] = useState('inbox');
  const exam = EXAMS[examType] || EXAMS.dsat;
  return (
    <div className="max-w-6xl mx-auto p-6" data-testid="tutor-exam-review-page">
      <div className="mb-4">
        <div className="flex items-center gap-2">
          <GraduationCap className={`h-5 w-5 text-${exam.accent}`} />
          <h1 className="text-xl font-bold text-gray-900">{exam.label} — Tutor Workspace</h1>
        </div>
        <div className="text-xs text-gray-600 mt-1">
          You see only students you are the assigned tutor for. Attempt records are immutable — feedback lives alongside.
        </div>
      </div>

      {/* Exam-type tabs */}
      <div className="flex items-center gap-1 mb-3" data-testid="tutor-exam-type-tabs">
        {Object.entries(EXAMS).map(([key, cfg]) => (
          <button
            key={key}
            onClick={() => setExamType(key)}
            data-testid={`tutor-exam-tab-${key}`}
            className={`px-3 py-1.5 rounded-full text-xs font-bold border inline-flex items-center ${
              examType === key
                ? `bg-${cfg.accent} text-white border-transparent`
                : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
            }`}
          >
            {cfg.label}
          </button>
        ))}
      </div>

      <div className="flex items-center gap-1 mb-4">
        <TabBtn active={tab === 'roster'} onClick={() => setTab('roster')} testid="tutor-tab-roster">
          <Users className="h-3.5 w-3.5 mr-1" /> Assigned Students
        </TabBtn>
        <TabBtn active={tab === 'inbox'} onClick={() => setTab('inbox')} testid="tutor-tab-inbox">
          <Inbox className="h-3.5 w-3.5 mr-1" /> Pending Reviews
        </TabBtn>
        {examType === 'dsat' && (
          <TabBtn active={tab === 'assignments'} onClick={() => setTab('assignments')} testid="tutor-tab-assignments">
            <ClipboardList className="h-3.5 w-3.5 mr-1" /> Assign a test
          </TabBtn>
        )}
      </div>

      {tab === 'roster' && <StudentRoster examType={examType} />}
      {tab === 'inbox' && <ReviewInbox examType={examType} />}
      {tab === 'assignments' && examType === 'dsat' && <AdminAssignmentsPanel />}
    </div>
  );
}

function TabBtn({ active, onClick, testid, children }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`px-3 py-1.5 rounded-full text-xs font-bold border inline-flex items-center ${
        active ? 'bg-gray-900 text-white border-gray-900' : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
      }`}
    >
      {children}
    </button>
  );
}

function StudentRoster({ examType }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedEmail, setSelectedEmail] = useState(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const r = await ax.get(`/api/exam/v2/tutor/students?exam_type=${examType}`);
      setItems(r.data?.items || []);
    } catch (e) { toast.error(e.response?.data?.detail || 'Load failed'); }
    finally { setLoading(false); }
  }, [examType]);
  useEffect(() => { setSelectedEmail(null); refresh(); }, [refresh]);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[minmax(300px,400px),1fr] gap-4">
      <Card className="p-3">
        <div className="flex items-center justify-between mb-2">
          <div className="text-xs font-bold text-gray-800">Assigned students ({items.length})</div>
          <Button size="sm" variant="outline" onClick={refresh} data-testid="tutor-roster-refresh">
            <RefreshCcw className="h-3.5 w-3.5" />
          </Button>
        </div>
        {loading ? (
          <div className="p-4 text-center text-gray-500 text-xs"><Loader2 className="h-4 w-4 animate-spin inline mr-1" /> Loading…</div>
        ) : items.length === 0 ? (
          <div className="p-4 text-center text-xs text-gray-500 italic">
            No students yet in this exam.
          </div>
        ) : (
          <div className="space-y-1 max-h-[520px] overflow-y-auto">
            {items.map((s) => (
              <button
                key={s.student_email}
                onClick={() => setSelectedEmail(s.student_email)}
                className={`w-full text-left px-3 py-2 rounded border ${selectedEmail === s.student_email ? 'border-gray-900 bg-gray-50' : 'border-gray-200 hover:bg-gray-50'}`}
                data-testid={`tutor-roster-${s.student_email}`}
              >
                <div className="font-semibold text-gray-900 text-sm">{s.student_name || s.student_email}</div>
                <div className="text-[11px] text-gray-500">{s.student_email}</div>
                <div className="flex items-center gap-2 mt-1 flex-wrap text-[10px]">
                  {s.attempts_awaiting_review > 0 && (
                    <Badge className="bg-blue-100 text-blue-800">{s.attempts_awaiting_review} to review</Badge>
                  )}
                  {s.assignments_overdue > 0 && (
                    <Badge className="bg-rose-100 text-rose-800">{s.assignments_overdue} overdue</Badge>
                  )}
                  {s.assignments_open > 0 && (
                    <Badge className="bg-amber-100 text-amber-800">{s.assignments_open} open</Badge>
                  )}
                  {s.best_score_pct != null && (
                    <Badge className="bg-emerald-100 text-emerald-800">Best {s.best_score_pct}%</Badge>
                  )}
                </div>
              </button>
            ))}
          </div>
        )}
      </Card>

      <Card className="p-3">
        {selectedEmail ? <StudentInsights email={selectedEmail} examType={examType} /> : (
          <div className="p-6 text-center text-sm text-gray-500">Choose a student on the left to see their {EXAMS[examType].label} insights.</div>
        )}
      </Card>
    </div>
  );
}

function StudentInsights({ email, examType }) {
  const [insights, setInsights] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let live = true;
    (async () => {
      setLoading(true);
      try {
        const r = await ax.get(`/api/exam/v2/tutor/student/${encodeURIComponent(email)}/insights?exam_type=${examType}`);
        if (live) setInsights(r.data);
      } catch (e) { toast.error(e.response?.data?.detail || 'Load failed'); }
      finally { if (live) setLoading(false); }
    })();
    return () => { live = false; };
  }, [email, examType]);

  if (loading) return <div className="p-6 text-center text-gray-500"><Loader2 className="h-4 w-4 animate-spin inline mr-2" /> Loading…</div>;
  if (!insights) return null;

  return (
    <div className="space-y-3" data-testid={`tutor-insights-${email}`}>
      <div className="flex items-baseline gap-2">
        <div className="text-sm font-bold text-gray-900">{email}</div>
        {insights.average_pct != null && (
          <Badge className="bg-emerald-100 text-emerald-800">Avg {insights.average_pct}%</Badge>
        )}
        <Badge className="bg-gray-100 text-gray-700">{insights.attempts_total} attempts</Badge>
        {insights.attempts_awaiting_review > 0 && (
          <Badge className="bg-blue-100 text-blue-800">{insights.attempts_awaiting_review} to review</Badge>
        )}
      </div>

      <div>
        <div className="text-[10px] uppercase tracking-wider text-gray-500 font-bold mb-1">Recent attempts</div>
        <div className="space-y-1 max-h-96 overflow-y-auto">
          {insights.attempts.map((a) => {
            const s = a.score || {};
            const pct = s.percentage ?? s.total_pct;
            const rev = EXAMS[examType].reviewRoute;
            return (
              <div key={a.attempt_id} className="border rounded p-2 text-[12px] flex items-center gap-2" data-testid={`tutor-attempt-${a.attempt_id}`}>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-gray-900 truncate">{a.test_title}</div>
                  <div className="text-[10px] text-gray-500">
                    {(a.test_type || '').replace('_', ' ')} · {a.section?.toUpperCase() || '—'} · {a.submitted_at ? new Date(a.submitted_at).toLocaleString() : '—'}
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-sm font-bold text-emerald-700">{pct != null ? `${pct}%` : (a.score != null ? `${a.score}/${a.max_score ?? '?'}` : '—')}</div>
                  <div className="text-[10px] text-gray-500">{a.review_status === 'reviewed' ? 'Reviewed' : (a.requires_review ? 'Pending' : 'Auto-scored')}</div>
                </div>
                {rev && (
                  <Link to={rev(a.attempt_id)} className="ml-1" data-testid={`tutor-open-${a.attempt_id}`}>
                    <Button size="sm" variant="outline"><Eye className="h-3.5 w-3.5 mr-1" /> Open</Button>
                  </Link>
                )}
              </div>
            );
          })}
          {insights.attempts.length === 0 && (
            <div className="text-[11px] text-gray-500 italic">No submitted attempts yet.</div>
          )}
        </div>
      </div>
    </div>
  );
}

function ReviewInbox({ examType }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [feedbackByAttempt, setFeedbackByAttempt] = useState({});
  const [statusFilter, setStatusFilter] = useState('new');
  const [studentFilter, setStudentFilter] = useState('all');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await ax.get(`/api/exam/v2/tutor/reviews-inbox?exam_type=${examType}&status=${statusFilter}`);
      setItems(r.data?.items || []);
    } catch (e) { toast.error(e.response?.data?.detail || 'Load failed'); }
    finally { setLoading(false); }
  }, [statusFilter, examType]);
  useEffect(() => { load(); }, [load]);

  // Read ?student=email from URL once so notification deep-links land pre-filtered
  useEffect(() => {
    const p = new URLSearchParams(window.location.search).get('student');
    if (p) setStudentFilter(p.toLowerCase());
  }, []);

  const studentOptions = useMemo(() => {
    const seen = new Map();
    for (const a of items) {
      const em = (a.student_email || '').toLowerCase();
      if (!em) continue;
      if (!seen.has(em)) seen.set(em, a.student_name || em);
    }
    return Array.from(seen, ([email, name]) => ({ email, name })).sort((a, b) => a.name.localeCompare(b.name));
  }, [items]);
  const filteredItems = useMemo(() => {
    if (studentFilter === 'all') return items;
    return items.filter((a) => (a.student_email || '').toLowerCase() === studentFilter);
  }, [items, studentFilter]);

  const submitFeedback = async (attempt_id, marked_reviewed) => {
    const comment = feedbackByAttempt[attempt_id] || '';
    try {
      await ax.post(`/api/exam/v2/attempts/${attempt_id}/review`, {
        comment: comment || null,
        marked_reviewed: !!marked_reviewed,
      });
      toast.success(marked_reviewed ? 'Marked reviewed' : 'Feedback saved');
      setFeedbackByAttempt((m) => ({ ...m, [attempt_id]: '' }));
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
  };

  const isPte = examType === 'pte_academic';

  return (
    <Card className="p-3" data-testid="tutor-review-inbox">
      <div className="flex flex-wrap items-center gap-2 mb-2">
        <div className="text-xs font-bold text-gray-800">
          {isPte ? 'PTE submissions awaiting review' : 'Attempts awaiting your feedback'}
        </div>
        <select className="border rounded px-2 py-1 text-xs h-8 ml-auto"
          value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
          data-testid="tutor-inbox-filter">
          <option value="new">Pending only</option>
          <option value="reviewed">Reviewed</option>
          <option value="">All</option>
        </select>
        {studentOptions.length > 0 && (
          <select className="border rounded px-2 py-1 text-xs h-8"
            value={studentFilter} onChange={(e) => setStudentFilter(e.target.value)}
            data-testid="tutor-inbox-student-filter">
            <option value="all">All students ({studentOptions.length})</option>
            {studentOptions.map((s) => (
              <option key={s.email} value={s.email}>{s.name}</option>
            ))}
          </select>
        )}
        <Button size="sm" variant="outline" onClick={load}><RefreshCcw className="h-3.5 w-3.5" /></Button>
      </div>
      {loading ? (
        <div className="p-4 text-center text-xs text-gray-500"><Loader2 className="h-4 w-4 animate-spin inline mr-1" /> Loading…</div>
      ) : filteredItems.length === 0 ? (
        <div className="p-4 text-center text-xs text-gray-500 italic">
          {studentFilter === 'all' ? 'All caught up.' : `No matches for the selected student.`}
        </div>
      ) : (
        <div className="space-y-2 max-h-[640px] overflow-y-auto">
          {filteredItems.map((a) => isPte
            ? <PTEInboxRow key={a.attempt_id} a={a}
                feedbackValue={feedbackByAttempt[a.attempt_id] || ''}
                onFeedbackChange={(v) => setFeedbackByAttempt((m) => ({ ...m, [a.attempt_id]: v }))}
                onSave={() => submitFeedback(a.attempt_id, false)}
                onDone={() => submitFeedback(a.attempt_id, true)} />
            : <DSATInboxRow key={a.attempt_id} a={a}
                feedbackValue={feedbackByAttempt[a.attempt_id] || ''}
                onFeedbackChange={(v) => setFeedbackByAttempt((m) => ({ ...m, [a.attempt_id]: v }))}
                onSave={() => submitFeedback(a.attempt_id, false)}
                onDone={() => submitFeedback(a.attempt_id, true)} />
          )}
        </div>
      )}
    </Card>
  );
}

function DSATInboxRow({ a, feedbackValue, onFeedbackChange, onSave, onDone }) {
  const s = a.score || {};
  const pct = s.percentage ?? s.total_pct;
  return (
    <div className="border rounded p-2 text-[12px]" data-testid={`tutor-inbox-row-${a.attempt_id}`}>
      <div className="flex items-center gap-3">
        <div className="flex-1 min-w-0">
          <div className="font-semibold text-gray-900 truncate">{a.test_title}</div>
          <div className="text-[10px] text-gray-500">
            {a.student_name || a.student_email} · {a.section?.toUpperCase() || '—'} · Score {pct ?? '—'}% · {a.submitted_at ? new Date(a.submitted_at).toLocaleString() : '—'}
          </div>
        </div>
        <Link to={`/dsat/attempt/${a.attempt_id}/review`} data-testid={`tutor-inbox-open-${a.attempt_id}`}>
          <Button size="sm" variant="outline"><Eye className="h-3.5 w-3.5 mr-1" /> Open</Button>
        </Link>
      </div>
      <FeedbackControls attempt_id={a.attempt_id} value={feedbackValue}
        onChange={onFeedbackChange} onSave={onSave} onDone={onDone} />
    </div>
  );
}

function PTEInboxRow({ a, feedbackValue, onFeedbackChange, onSave, onDone }) {
  const isSpeaking = (a.question_type || '').startsWith('pte_') && (
    ['pte_read_aloud','pte_repeat_sentence','pte_describe_image','pte_retell_lecture','pte_answer_short_question'].includes(a.question_type)
  );
  const isWriting = ['pte_summarize_written_text','pte_write_essay','pte_summarize_spoken_text','pte_write_from_dictation'].includes(a.question_type);
  const writingText = typeof a.response === 'string' ? a.response : '';
  const wordCount = writingText ? writingText.trim().split(/\s+/).filter(Boolean).length : 0;
  return (
    <div id={`attempt-${a.attempt_id}`} className="border rounded p-3 text-[12px] bg-white" data-testid={`tutor-inbox-row-${a.attempt_id}`}>
      <div className="flex items-center gap-3">
        <div className="flex-1 min-w-0">
          <div className="font-semibold text-gray-900 truncate">{a.test_title}</div>
          <div className="text-[10px] text-gray-500">
            {a.student_name || a.student_email} · {a.section?.toUpperCase() || '—'} · {a.submitted_at ? new Date(a.submitted_at).toLocaleString() : '—'}
          </div>
        </div>
        {isSpeaking && <Badge className="bg-emerald-100 text-emerald-800"><Mic className="h-3 w-3 mr-1" />Speaking</Badge>}
        {isWriting && <Badge className="bg-amber-100 text-amber-800"><PenTool className="h-3 w-3 mr-1" />Writing</Badge>}
      </div>

      {/* Recording */}
      {a.recording_url && (
        <div className="mt-2" data-testid={`tutor-inbox-audio-${a.attempt_id}`}>
          <audio controls preload="none" className="w-full h-9">
            <source src={`${BACKEND_URL}${a.recording_url}`} />
          </audio>
        </div>
      )}

      {/* Written response */}
      {isWriting && writingText && (
        <div className="mt-2 border rounded p-2 bg-gray-50 whitespace-pre-wrap text-[12px]"
             data-testid={`tutor-inbox-writing-${a.attempt_id}`}>
          <div className="text-[10px] uppercase tracking-wider text-gray-500 font-bold mb-1">
            Student response · {wordCount} words
          </div>
          {writingText}
        </div>
      )}

      <FeedbackControls attempt_id={a.attempt_id} value={feedbackValue}
        onChange={onFeedbackChange} onSave={onSave} onDone={onDone} />
    </div>
  );
}

function FeedbackControls({ attempt_id, value, onChange, onSave, onDone }) {
  return (
    <div className="mt-2 flex items-start gap-2">
      <Textarea
        rows={2} placeholder="Overall feedback (optional)"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        data-testid={`tutor-inbox-fb-${attempt_id}`}
        className="text-xs"
      />
      <div className="flex flex-col gap-1">
        <Button size="sm" onClick={onSave}
          className="bg-blue-700 hover:bg-blue-800 text-white"
          data-testid={`tutor-inbox-save-${attempt_id}`}>
          <MessageSquare className="h-3.5 w-3.5 mr-1" /> Save
        </Button>
        <Button size="sm" onClick={onDone}
          className="bg-emerald-700 hover:bg-emerald-800 text-white"
          data-testid={`tutor-inbox-done-${attempt_id}`}>
          <CheckCircle2 className="h-3.5 w-3.5 mr-1" /> Mark reviewed
        </Button>
      </div>
    </div>
  );
}
